import { css, SerializedStyles } from '@emotion/react'
import BlockIcon from '@mui/icons-material/Block'
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import {
    FunctionComponent,
    HTMLAttributes,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import type { FloaterModal, PMChatsState } from '../../../../types/mfc'
import { openMessage } from '../../../actions/message'
import {
    goToMessages,
    goToModelsYouMayLike,
    goToRooms,
} from '../../../actions/nav'
import { openRoom } from '../../../actions/room'
import {
    openAboutDrawer,
    openAccountDrawer,
    openAuthenticationDrawer,
    openContactSupportDrawer,
    openHiddenModelsDrawer,
    openIgnoredModelsDrawer,
    openMyAccountDrawer,
    openSettingsDrawer,
    openSignupDrawer,
    openUserFeedbackDrawer,
} from '../../../app-state/modals/state.drawer'
import { minimizeFloater } from '../../../app-state/modals/state.floater'
import { ListType, SecureStateType } from '../../../constants/enums'
import { QUICK_REPLY } from '../../../constants/floaters'
import { logout } from '../../../mfcLogic/auth'
import { APPSTATE } from '../../../state/state.app'
import i18n from '../../../utilities/i18n'
import { capitalize } from '../../../utilities/string'
import {
    motionTransition,
    TransitionTypes,
} from '../../../utilities/transitions'
import ButtonDown from '../../Buttons/ButtonDown'
import ButtonUp from '../../Buttons/ButtonUp'
import IconButton, { IconButtonProps } from '../../Buttons/IconButton'
import BadgeAvatar from '../../common/BadgeAvatar'
import { useLayoutSelector } from '../../Hooks/useLayout'
import { useList } from '../../Hooks/useList'
import { useMailStateSelector } from '../../Hooks/useMail'
import { useDialogSelector, useFloaters } from '../../Hooks/useModal'
import { usePMSelector } from '../../Hooks/usePM'
import { usePortalStateSelector } from '../../Hooks/usePortalState'
import { useRoomsCount } from '../../Hooks/useRooms'
import { useRouteSelector } from '../../Hooks/useRoute'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useSecureState } from '../../Hooks/useSecureState'
import { useUserSelector } from '../../Hooks/useUser'
import { IconProps } from '../../Icons'
import AccountCircleOutlined from '../../Icons/AccountCircleOutlined'
import Archives from '../../Icons/Archives'
import Explore from '../../Icons/Explore'
import Friend from '../../Icons/Friend'
import Home from '../../Icons/Home'
import Mail from '../../Icons/Mail'
import Message from '../../Icons/Message'
import More from '../../Icons/More'
import Newspaper from '../../Icons/Newspaper'
import Note from '../../Icons/Note'
import PrivateGallery from '../../Icons/PrivateGallery'
import Recommendation from '../../Icons/Recommendation'
import Room from '../../Icons/Room'
import Settings from '../../Icons/Settings'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import {
    APP_FONT,
    COMPONENT_HEIGHTS,
    COMPONENT_WIDTHS,
    PAPER_HEIGHTS,
} from '../../theme'
import Link from '../Link'
import Notification from '../Notification'

const navRootStyles = css({
    position: 'relative',
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
})

const navButtonStyles = css({
    width: '100%',
    paddingRight: 8,
    color: palette.navButton,
    borderRadius: 0,
    border: 0,
    letterSpacing: 'normal',
    overflow: 'unset',
    textWrap: 'wrap',
    '& span:last-of-type': {
        borderRadius: '50%',
    },
})

const getButtonHeight = (leftNav: boolean) => {
    if (leftNav) return COMPONENT_HEIGHTS.navExpanded
    return COMPONENT_HEIGHTS.navBottom - 1
}

interface IStyledIconProps extends IconButtonProps {
    expanded: boolean
    leftNav: boolean
}

const StyledIconButton = (props: IStyledIconProps) => {
    const { expanded, leftNav, children, ...directProps } = props
    return (
        <IconButton
            {...directProps}
            css={navButtonStyles}
            style={{
                flexDirection: expanded ? 'row' : 'column',
                flex: 1,
                justifyContent: expanded ? 'flex-start' : 'center',
                height: leftNav
                    ? getButtonHeight(leftNav)
                    : `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom - 1}px)`,
                paddingTop: !leftNav ? 0 : 8,
                paddingLeft: expanded ? 16 : 8,
                paddingBottom: !leftNav ? 'var(--saib)' : 8,
            }}
        >
            {children}
        </IconButton>
    )
}

interface IStyledButtonTitle {
    active: boolean
    expanded: boolean
    quickReply?: boolean
    leftNav: boolean
    labelText?: string
}

const StyledButtonTitle = (props: IStyledButtonTitle) => {
    const { active, expanded, quickReply, leftNav, labelText } = props
    return (
        <span
            style={{
                position: leftNav && !expanded ? 'absolute' : 'initial',
                top: leftNav && !expanded ? 37 : undefined,
                marginLeft: expanded ? 24 : 0,
                fontFamily: APP_FONT,
                fontSize: expanded ? 14 : !leftNav ? 9 : 7,
                fontWeight: expanded ? 600 : 500,
                lineHeight: expanded ? 24 / 14 : !leftNav ? '10px' : '8px',
                color: active ? palette.contrastText : palette.navButton,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth:
                    quickReply && expanded ? 110 : quickReply ? 52 : 'unset',
                paddingTop: quickReply ? getThemeSpacing(0.5) : 'unset',
            }}
        >
            {labelText}
        </span>
    )
}

interface NavButtonProps extends HTMLAttributes<HTMLDivElement> {
    leftNav: boolean
    children?: ReactNode
}

const StyledNavButtonContainer = (props: NavButtonProps) => {
    const { leftNav, children, ...divProps } = props
    return (
        <div {...divProps} style={{ flex: leftNav ? 0 : 1, zIndex: 1 }}>
            {children}
        </div>
    )
}

const upDownButtonContainerStyles = {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    zIndex: 2020,
} as const

const upButtonContainerStyles = css({
    ...upDownButtonContainerStyles,
    top: 0,
    '&:after': {
        width: '100%',
        height: 20,
        pointerEvents: 'none',
        content: '""',
        background: `linear-gradient(to bottom, ${palette.navBackground} 20%, rgba(255, 255, 255, 0) 80%)`,
    },
})
const downButtonContainerStyles = css({
    ...upDownButtonContainerStyles,
    bottom: 0,
    '&:before': {
        width: '100%',
        height: 20,
        pointerEvents: 'none',
        content: '""',
        background: `linear-gradient(to top, ${palette.navBackground} 20%, rgba(255, 255, 255, 0) 80%)`,
    },
})

const arrowButtonWrapperStyles = css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    backgroundColor: palette.navBackground,
    '& button': { width: 40, height: 40 },
})

interface IButton {
    onClick(): void
}

const DownButton = memo(({ onClick }: IButton) => (
    <div css={downButtonContainerStyles}>
        <div css={arrowButtonWrapperStyles}>
            <ButtonDown onClick={onClick} />
        </div>
    </div>
))

const UpButton = memo(({ onClick }: IButton) => (
    <div css={upButtonContainerStyles}>
        <div css={arrowButtonWrapperStyles}>
            <ButtonUp onClick={onClick} />
        </div>
    </div>
))

const StyledHr = ({ expanded }) => {
    return (
        <hr
            css={{
                width: 'calc(100% - 40px)',
                height: 1,
                marginTop: 0,
                marginBottom: 0,
                border: 'none',
                borderTop: `1px solid ${palette.buttonBorder}`,
                opacity: expanded ? 1 : 0,
            }}
        />
    )
}

const linkStyles = css({
    display: 'block',
})

interface IUnreadMessages {
    hide?: boolean
    leftNav: boolean
    unreadCount?: number
}

const UnreadMessages = memo((props: IUnreadMessages) => {
    const { hide, leftNav, unreadCount } = props
    return (
        <div
            css={{
                position: 'absolute',
                display: hide ? 'none' : 'block',
                top: leftNav ? 6 : undefined,
                bottom: leftNav
                    ? undefined
                    : `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom - 1}px / 1.75)`,
                left: leftNav ? 29 : undefined,
                right: leftNav ? undefined : 'calc(50% - 24px)',
                zIndex: PAPER_HEIGHTS.base,
            }}
        >
            <Notification value={unreadCount} />
        </div>
    )
})

interface INavButton {
    active?: boolean
    disabled?: boolean
    expanded?: boolean
    filled?: boolean
    Header?: ReactNode
    Icon: FunctionComponent<IconProps>
    label: string
    leftNav: boolean
    onClick?(e?: any): void
    styles?: SerializedStyles
    title?: string
    tooltip?: string
}

const NavButton = memo((props: INavButton) => {
    const {
        active,
        disabled,
        expanded,
        filled,
        Header,
        Icon,
        label,
        leftNav,
        onClick,
        styles,
        title,
        tooltip,
    } = props
    /// this isnt even working so let's shelve it for the moment'
    // <StyledTooltip
    //     title={title ?? i18n.t(`nav.expanded.${label}`)}
    //     disableHoverListener={true}
    //     placement="right"
    // >
    // </StyledTooltip>
    return (
        <div
            css={[
                {
                    position: 'relative', // containing block for absolute positioned children (e.g. notification)
                    height: leftNav
                        ? getButtonHeight(leftNav)
                        : `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom - 1}px)`,
                    width: '100%',
                    overflow: 'hidden',
                },
                styles,
            ]}
            title={tooltip}
        >
            {Header}

            <StyledIconButton
                onClick={!disabled ? onClick : undefined}
                data-testid={`NavButton__${capitalize(label)}`}
                expanded={expanded}
                leftNav={leftNav}
                disabled={disabled}
            >
                <Icon
                    color={
                        disabled
                            ? palette.disabledIcons
                            : active
                              ? palette.contrastIcons
                              : 'currentColor'
                    }
                    {...(filled || active ? { filled: true } : {})}
                />
                <StyledButtonTitle
                    active={active}
                    expanded={expanded}
                    leftNav={leftNav}
                    labelText={
                        title ??
                        i18n.t(
                            `nav.${
                                !expanded ? 'compact' : 'expanded'
                            }.${label}`,
                        )
                    }
                ></StyledButtonTitle>
            </StyledIconButton>
        </div>
    )
})

const ephemeralChatNotificationStyles = css({
    position: 'absolute',
    zIndex: 999,
    bottom: 8,
    right: 10,
    transform: 'scale(0.7)',
})
const hiddenOverflow = css({
    overflow: 'hidden',
})

interface IEphemeralChat {
    expandedNav: boolean
    leftNav: boolean
    pm: FloaterModal
    messages: PMChatsState
    vertical: boolean
}

const EphemeralChat = memo(
    ({ pm, expandedNav, leftNav, messages, vertical }: IEphemeralChat) => {
        const { id, minimized } = pm
        const name = useUserSelector(id, 'name')
        const offline = useUserSelector(id, 'isOffline')

        const unread = messages[id]?.unread ?? 0
        const chatClick = useCallback(() => {
            // don't allow mobile landscape to pop open quick replies
            return !vertical && APPSTATE.app.isMobile
                ? openMessage(id, TransitionTypes.FLOATER)
                : motionTransition(
                      () => minimizeFloater(id, QUICK_REPLY, !minimized),
                      {
                          id,
                          transitionSource: minimized
                              ? TransitionTypes.NAV
                              : TransitionTypes.FLOATER,
                          transitionDestination: minimized
                              ? TransitionTypes.FLOATER
                              : TransitionTypes.NAV,
                      },
                  )
        }, [id, minimized, vertical])
        return (
            <StyledNavButtonContainer leftNav={leftNav} onClick={chatClick}>
                <div css={hiddenOverflow}>
                    <StyledIconButton
                        // onClick={() =>
                        //     minimizeFloater(id, QUICK_REPLY, !minimized)
                        // }
                        data-testid={`NavButton__EphemeralChat`}
                        expanded={expandedNav}
                        leftNav={leftNav}
                    >
                        <BadgeAvatar
                            offline={offline}
                            alt={name}
                            size={24}
                            id={id}
                            disableDrag
                        />
                        {unread > 0 && (
                            <Notification
                                styleOverrides={ephemeralChatNotificationStyles}
                                value={unread}
                            />
                        )}
                        <StyledButtonTitle
                            active={!minimized}
                            expanded={expandedNav}
                            leftNav={leftNav}
                            quickReply
                            labelText={name}
                        ></StyledButtonTitle>
                    </StyledIconButton>
                </div>
            </StyledNavButtonContainer>
        )
    },
)

const EphemeralChats = memo(
    (props: Omit<IEphemeralChat, 'pm' | 'vertical'>) => {
        const { expandedNav } = props
        const floaters = useFloaters()
        const vertical = useScreenStateSelector('portrait')

        const pmFloaters = floaters.filter(
            (floater) => floater.show && floater.type === QUICK_REPLY,
        )
        const hasEphemeralChats = pmFloaters.length > 0
        if (!hasEphemeralChats) return null
        return (
            <>
                <StyledHr expanded={expandedNav} />
                {pmFloaters.map((pm) => (
                    <EphemeralChat
                        key={`${pm.id}-ephemeral-chat`}
                        pm={pm}
                        vertical={vertical}
                        {...props}
                    />
                ))}
            </>
        )
    },
)

interface INav {
    authenticated: boolean
    isMobile: boolean
    vertical: boolean
}

const Nav = memo((props: INav) => {
    const { authenticated, isMobile, vertical } = props
    const accountLevel = useUserSelector('me', 'level')
    const ref = useRef<HTMLDivElement>(null)
    const [scrollStart, setScrollStart] = useState(true)
    const [scrollEnd, setScrollEnd] = useState(true)

    const showDialog = useDialogSelector('show')
    const expanded = useLayoutSelector('expandedNav')
    const composeFocused = usePortalStateSelector('composeFocused')
    const quietFocus = usePortalStateSelector('quietFocus')
    const shouldShrinkInset = composeFocused && !quietFocus
    const leftNav = !vertical
    const expandedNav = expanded && leftNav
    const openRoomsCount = useRoomsCount()
    const { ageGateActive } = useSecureState(SecureStateType.AGE_VERIFICATION)
    const allMessages = usePMSelector('chats')
    const unreadMessagesCount = usePMSelector('unread')
    const activeMessageID = usePMSelector('activeID')
    const unreadMailCount = useMailStateSelector('unread')

    const openedRooms = useList(ListType.LAST_VIEWED, { returnIDs: false })
    const messagesList = Object.keys(allMessages)
    const singleOpenedRoom =
        openedRooms?.length === 1 ? openedRooms[0]?.slug : ''
    const singlePMInQueue = messagesList.length === 1

    const archives = 'chat-archives'
    const messages = 'messages'
    const message = 'message'
    const friends = 'friends'
    const search = 'search'
    const notes = 'notes'
    const modelsYouMayLike = 'recommendations'
    const rooms = 'rooms'
    const room = 'room'
    const mail = 'mail'
    const news = 'news'
    const home = ''
    const privateGallery = 'private-gallery'

    const route = useRouteSelector('visiblePage')
    const inRoomOrMessage = route === room || route === message
    const isArchives = route === archives
    const isMessages = route === messages || route === message
    const isModelsYouMayLike = route === modelsYouMayLike
    const isRooms = route === rooms || route === room
    const isFriends = route === friends
    const isSearch = route === search
    const isNotes = route === notes
    const isMail = route === mail
    const isNews = route === news
    const isHome = route === home
    const isPrivateGallery = route === privateGallery

    // if we're in a room then we obviously have at least 1 room open
    const safeOpenRoomCount =
        route === room ? Math.max(openRoomsCount, 1) : openRoomsCount
    const showSettingsInsteadOfHome = !leftNav && isHome
    // const { windowHeight } = useScreenState()
    const windowHeight = useScreenStateSelector('windowHeight')

    const [showMessagesNotification, setShowMessagesNotification] =
        useState(false)

    // *** must set in useEffect to avoid rehydration issues ***
    // (until we can render the correct notification icon on the server)
    useEffect(() => {
        setShowMessagesNotification(!isMessages && unreadMessagesCount > 0)
    }, [isMessages, unreadMessagesCount])

    useEffect(() => {
        scrollCheck()
    }, [windowHeight])

    const RoomNotification = (
        <UnreadMessages
            hide={!safeOpenRoomCount || safeOpenRoomCount < 1}
            leftNav={leftNav}
            unreadCount={safeOpenRoomCount}
        />
    )

    const MessageNotification = (
        <UnreadMessages
            hide={!showMessagesNotification}
            leftNav={leftNav}
            unreadCount={unreadMessagesCount}
        />
    )

    const MailNotification = (
        <UnreadMessages
            hide={!unreadMailCount || unreadMailCount === 0}
            leftNav={leftNav}
            unreadCount={unreadMailCount}
        />
    )

    const onClickModelsYouMayLike = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        goToModelsYouMayLike('')
    }, [])

    const onClickRooms = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!isRooms && singleOpenedRoom) {
                openRoom(singleOpenedRoom)
            } else {
                goToRooms()
            }
        },
        [isRooms, singleOpenedRoom],
    )

    const onClickMessages = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!isMessages && singlePMInQueue && activeMessageID) {
                openMessage(activeMessageID, TransitionTypes.NONE)
            } else {
                goToMessages()
            }
        },
        [isMessages, singlePMInQueue, activeMessageID],
    )

    const checkScrollStart = useCallback((scrollTop?: number) => {
        if (typeof scrollTop === 'undefined' && !ref.current) return
        const scrollTopValue = scrollTop ?? ref.current?.scrollTop ?? 0
        const isScrollStart = scrollTopValue === 0
        setScrollStart(isScrollStart)
    }, [])

    const checkScrollEnd = useCallback(
        (scrollTop?: number, scrollHeight?: number, offsetHeight?: number) => {
            if (typeof scrollTop === 'undefined' && !ref.current) return
            const scrollTopValue = scrollTop ?? ref.current?.scrollTop ?? 0
            const scrollHeightValue =
                scrollHeight ?? ref.current?.scrollHeight ?? 0
            const offsetHeightValue =
                offsetHeight ?? ref.current?.offsetHeight ?? 0
            const isScrollEnd =
                scrollTopValue >= scrollHeightValue - offsetHeightValue
            setScrollEnd(isScrollEnd)
        },
        [],
    )

    const scrollCheck = useCallback(() => {
        checkScrollStart()
        checkScrollEnd()
    }, [])

    const slide = useCallback((shift: number) => {
        if (!ref.current) return
        // read layout properties before scroll
        const { scrollTop, scrollHeight, offsetHeight } = ref.current
        ref.current.scrollBy({ top: shift, behavior: 'smooth' })
        // pass in computed values to avoid reflow (https://gist.github.com/paulirish/5d52fb081b3570c81e3a)
        const computedScrollTop = Math.max(0, scrollTop + shift)
        checkScrollStart(computedScrollTop)
        checkScrollEnd(computedScrollTop, scrollHeight, offsetHeight)
    }, [])

    const onClickUp = useCallback(() => {
        slide(-200)
    }, [])

    const onClickDown = useCallback(() => {
        slide(200)
    }, [])

    const buttonWrapperStyles = css({
        position: 'relative', // containing block for absolute positioned children (e.g. notification)
        width: '100%',
        height: leftNav
            ? getButtonHeight(leftNav)
            : `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom - 1}px)`,
    })

    const EphemeralChatButtons = (
        <EphemeralChats
            expandedNav={expandedNav}
            leftNav={leftNav}
            messages={allMessages}
        />
    )

    const HomeButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page="home" disabled={ageGateActive}>
                <NavButton
                    label="home"
                    Icon={Home}
                    active={isHome}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.home')}
                    disabled={ageGateActive}
                    styles={buttonWrapperStyles}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const MyAccountButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="myAccount"
                Icon={AccountCircleOutlined}
                onClick={openMyAccountDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.myAccount')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const SettingsButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="settings"
                Icon={Settings}
                onClick={openSettingsDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.settings')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const ExploreButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={search} disabled={ageGateActive}>
                <NavButton
                    label="explore"
                    Icon={Explore}
                    active={isSearch}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.explore')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const FriendsButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={friends} disabled={ageGateActive}>
                <NavButton
                    label="friends"
                    Icon={Friend}
                    active={isFriends}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={'View and manage your friends.'}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const RoomsButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={rooms} disabled={ageGateActive}>
                <NavButton
                    label="rooms"
                    Icon={Room}
                    Header={RoomNotification}
                    onClick={onClickRooms}
                    active={isRooms}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.rooms')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const MessagesButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={messages} disabled={ageGateActive}>
                <NavButton
                    label="messages"
                    Icon={Message}
                    Header={MessageNotification}
                    {...(singlePMInQueue ? { onClick: onClickMessages } : {})}
                    active={isMessages}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.messages')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const MoreButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="more"
                Icon={More}
                onClick={openAccountDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.more')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const MailButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={mail} disabled={ageGateActive}>
                <NavButton
                    label="mail"
                    Icon={Mail}
                    Header={MailNotification}
                    active={isMail}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.mail')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const NewsButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={news} disabled={ageGateActive}>
                <NavButton
                    label="news"
                    Icon={Newspaper}
                    active={isNews}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.news')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const ModelsYouMayLikeButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link
                css={linkStyles}
                nav
                page={modelsYouMayLike}
                disabled={ageGateActive}
            >
                <NavButton
                    label="recommendations"
                    Icon={Recommendation}
                    active={isModelsYouMayLike}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    onClick={onClickModelsYouMayLike}
                    tooltip={i18n.t('nav.tooltips.recommendations')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const ArchivesButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={archives} disabled={ageGateActive}>
                <NavButton
                    label="archives"
                    Icon={Archives}
                    active={isArchives}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.archives')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const NotesButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link css={linkStyles} nav page={notes} disabled={ageGateActive}>
                <NavButton
                    label="notes"
                    Icon={Note}
                    active={isNotes}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.notes')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    const HiddenModelsButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="hiddenModels"
                Icon={VisibilityOffOutlinedIcon}
                onClick={openHiddenModelsDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.hiddenModels')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const IgnoredModelsButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="ignoredModels"
                Icon={BlockIcon}
                onClick={openIgnoredModelsDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.ignoredModels')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const SupportButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="support"
                Icon={ContactSupportOutlinedIcon}
                onClick={openContactSupportDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.support')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const FeedbackButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="feedback"
                Icon={FeedbackOutlinedIcon}
                onClick={openUserFeedbackDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.feedback')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const LogInButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="logIn"
                Icon={LoginIcon}
                onClick={openAuthenticationDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.logIn')}
            />
        </StyledNavButtonContainer>
    )

    const LogOutButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="logOut"
                Icon={LogoutIcon}
                onClick={logout}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.logOut')}
            />
        </StyledNavButtonContainer>
    )

    const SignUpButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="signUp"
                Icon={AccountCircleOutlined}
                onClick={openSignupDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.signUp')}
            />
        </StyledNavButtonContainer>
    )

    const AboutButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <NavButton
                label="about"
                Icon={InfoOutlinedIcon}
                onClick={openAboutDrawer}
                expanded={expandedNav}
                leftNav={leftNav}
                tooltip={i18n.t('nav.tooltips.about')}
                disabled={ageGateActive}
            />
        </StyledNavButtonContainer>
    )

    const PrivateGalleryButton = (
        <StyledNavButtonContainer leftNav={leftNav}>
            <Link
                css={linkStyles}
                nav
                page={privateGallery}
                disabled={ageGateActive}
            >
                <NavButton
                    label="privateGallery"
                    Icon={PrivateGallery}
                    active={isPrivateGallery}
                    expanded={expandedNav}
                    leftNav={leftNav}
                    tooltip={i18n.t('nav.tooltips.privateGallery')}
                    disabled={ageGateActive}
                />
            </Link>
        </StyledNavButtonContainer>
    )

    // console.log('[Nav]', { isMobile, leftNav, inRoomOrMessage, expandedNav })

    return (
        <div css={navRootStyles} id="nav">
            {leftNav && !scrollStart && <UpButton onClick={onClickUp} />}
            <div
                ref={ref}
                data-testid="Nav__Container"
                className={
                    shouldShrinkInset
                        ? 'shrink-inset no-scrollbars'
                        : 'no-scrollbars'
                }
                onScroll={scrollCheck}
                css={{
                    display: 'flex',
                    backgroundColor: palette.navBackground,
                    ...(leftNav
                        ? {
                              flexFlow: 'column nowrap',
                              justifyContent: !isMobile
                                  ? 'flex-start'
                                  : 'space-between',
                              height: '100%',
                              width: !expanded
                                  ? COMPONENT_WIDTHS.leftNav
                                  : COMPONENT_WIDTHS.leftNavExpanded,
                              minWidth: !expanded
                                  ? COMPONENT_WIDTHS.leftNav
                                  : COMPONENT_WIDTHS.leftNavExpanded,
                              maxWidth: !expanded
                                  ? COMPONENT_WIDTHS.leftNav
                                  : COMPONENT_WIDTHS.leftNavExpanded,
                              margin: '0 auto',
                              overflowY: 'auto',
                              scrollBehavior: 'smooth',
                          }
                        : {
                              flexFlow: 'row nowrap',
                              justifyContent: 'space-between',
                              width: '100%',
                              height: `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom}px)`,
                              minHeight: `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom}px)`,
                              maxHeight: `calc(var(--saib) + ${COMPONENT_HEIGHTS.navBottom}px)`,
                              overflow: 'hidden',
                              ...(!showDialog
                                  ? { zIndex: PAPER_HEIGHTS.footer }
                                  : {}),
                              borderTop: !inRoomOrMessage
                                  ? `1px solid ${palette.drawerDivider}`
                                  : `1px solid ${palette.navBackground}`, // "remove" border without changing height
                          }),
                }}
            >
                {showSettingsInsteadOfHome ? SettingsButton : HomeButton}
                {ExploreButton}
                {authenticated && FriendsButton}
                {RoomsButton}
                {MessagesButton}
                {!leftNav && MoreButton}
                {leftNav && authenticated && (
                    <>
                        {EphemeralChatButtons}
                        <StyledHr expanded={expanded} />
                        {SettingsButton}
                        {MyAccountButton}
                        {MailButton}
                        {NewsButton}
                        {ArchivesButton}
                        {NotesButton}
                        {authenticated && ModelsYouMayLikeButton}
                        {authenticated &&
                            accountLevel >= 2 &&
                            PrivateGalleryButton}
                        {expandedNav && (
                            <>
                                {HiddenModelsButton}
                                {IgnoredModelsButton}
                                <StyledHr expanded={expanded} />
                                {SupportButton}
                                {FeedbackButton}
                                {AboutButton}
                                {LogOutButton}
                            </>
                        )}
                        {!expandedNav && MoreButton}
                    </>
                )}
                {leftNav && !authenticated && (
                    <>
                        <StyledHr expanded={expanded} />
                        {SettingsButton}
                        {SignUpButton}
                        {LogInButton}
                    </>
                )}
            </div>
            {leftNav && !scrollEnd && <DownButton onClick={onClickDown} />}
        </div>
    )
})

export default Nav
